import VariantPicker from "@theme/variant-picker";
import { requestYieldCallback } from "@theme/utilities";
import { VariantSelectedEvent, VariantUpdateEvent } from "@theme/events";
import { morph } from "@theme/morph";

/**
 * A custom element that manages product selection within a section
 * Extends VariantPicker to reuse its fetch and morph logic
 * Allows users to switch between different products, updating the section content
 */
export default class ProductPicker extends VariantPicker {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();

    // Replace variant-picker's change handler with our own
    // Our handler will check if it's a product picker input, otherwise delegate to parent
    this.addEventListener("change", this.productChanged.bind(this));
  }

  variantChanged(event) {
    // No-op
  }

  /**
   * Handles the product change event.
   * Checks if it's a product picker input, otherwise delegates to variant-picker
   * @param {Event} event - The product change event.
   */
  productChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedInput = event.target;

    if (!(selectedInput instanceof HTMLInputElement)) {
      // Not a product picker input, delegate to variant-picker
      super.variantChanged(event);
      return;
    }

    // Check if this is a product picker input (has product data attributes)
    const productUrl = selectedInput.dataset.productUrl;

    if (!productUrl) {
      // Not a product picker input, delegate to variant-picker
      super.variantChanged(event);
      return;
    }

    // Get variant info from the data attributes
    const variantId = selectedInput.dataset.variantId;
    const productId = selectedInput.dataset.productId;
    const productHandle = selectedInput.dataset.productHandle;

    // Update dataset to match the new product
    if (productId) {
      this.dataset.productId = productId;
      this.dataset.currentProductId = productId;
    }

    // Update product URL
    if (productUrl) {
      // Remove query params if present to get clean product URL
      const cleanProductUrl = productUrl.includes("?")
        ? productUrl.split("?")[0]
        : productUrl;
      this.dataset.productUrl = cleanProductUrl;
    }

    // Build request URL - when morphing main, we use the product URL directly
    // The variant-picker's buildRequestUrl expects a selectedOption with dataset,
    // but we can build the URL directly since we're always morphing main
    let requestUrl = productUrl;
    if (requestUrl.includes("?")) {
      requestUrl += "&section_id=" + this.dataset.sectionId;
    } else {
      requestUrl += "?section_id=" + this.dataset.sectionId;
    }

    if (this.closest("product-card")) {
      return this.swapProductCard(productId);
    }

    // Always morph the main content when switching products
    this.fetchUpdatedSection(requestUrl, false);

    // Update URL if on product page
    if (variantId && productHandle) {
      const url = new URL(window.location.href);
      if (window.location.pathname.indexOf("/products/") !== -1) {
        url.searchParams.set("variant", variantId);
        url.pathname = `/products/${productHandle}`;

        if (url.href !== window.location.href) {
          requestYieldCallback(() => {
            history.replaceState({}, "", url.toString());
          });
        }
      }
    }
  }

  swapProductCard(productId) {
    let parentCard = this.closest("product-card");
    if (!parentCard) return;
    if (
      parentCard.parentElement &&
      parentCard.parentElement.tagName === "PRODUCT-CARD-LINK"
    ) {
      parentCard = parentCard.parentElement;
    }

    const toSwap = parentCard.nextElementSibling?.querySelector(
      `[data-product-id="${productId}"] product-card`
    );

    if (toSwap) {
      const clone = toSwap.cloneNode(true);
      parentCard.replaceWith(clone);
      clone.querySelector(
        `input[type="radio"][data-product-id="${productId}"]`
      ).checked = true;
    }
  }

  /**
   * Fetches the updated section using variant-picker's logic
   * Overridden to look for product-picker script tag instead of variant-picker
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // Abort the previous fetch request if it's still pending
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, "text/html");
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector("overflow-list[defer]")?.removeAttribute("defer");

        // Look for product-picker script tag instead of variant-picker
        const textContent = html.querySelector(
          `product-picker script[type="application/json"]`
        )?.textContent;

        if (shouldMorphMain) {
          // Use parent's updateMain method
          this.updateMain(html);
        } else {
          // If not morphing main, fall back to section-level morphing
          const sectionId = this.dataset.sectionId;
          if (sectionId) {
            const sectionIdFull = `shopify-section-${sectionId}`;
            const newSection = html.getElementById(sectionIdFull);
            const currentSection = document.getElementById(sectionIdFull);

            if (newSection && currentSection) {
              morph(currentSection, newSection);
            }
          }
        }

        // Dispatch events if we have variant data
        if (textContent) {
          try {
            const variantData = JSON.parse(textContent);
            // Dispatch variant update event similar to variant-picker

            this.dispatchEvent(
              new VariantUpdateEvent(variantData, variantData.id, {
                html,
                productId: this.dataset.productId ?? "",
                newProduct: {
                  id: this.dataset.productId ?? "",
                  url: this.dataset.productUrl ?? "",
                },
              })
            );
          } catch (e) {
            console.warn("Failed to parse variant JSON:", e);
          }
        }

        // Dispatch a custom event for any components that might be listening
        const productId = this.dataset.currentProductId;
        this.dispatchEvent(
          new CustomEvent("product-updated", {
            bubbles: true,
            detail: {
              productId,
            },
          })
        );
      })
      .catch((error) => {
        if (error.name === "AbortError") {
          console.warn("Fetch aborted by user");
        } else {
          console.error("Error fetching product:", error);
        }
      });
  }
}

if (!customElements.get("product-picker")) {
  customElements.define("product-picker", ProductPicker);
}
